<template>
  <ChartCard :data-widget-id="widgetId" :id-for-aria="widgetId">
    <ChartHeader
      :label="widgetLabel"
      :widget-download-id="widgetId"
      tooltip-key="executive-tth-bench"
      class="mb-2"
    />
    <ChartContent
      height="131px"
      :empty-data="emptyData || noWidgetData"
      :error="error"
      :loading="loading"
      type="horizontalBenchmarkChart"
      class="px-0 pb-0"
      :link="{
        name: 'Hiring Report',
        to: 'hiring-report',
      }"
    >
      <BarChart :id="`${widgetId}-chart`" :data="chartData" :options="chartOptions" :plugins="plugins" title="Time to hire vs Benchmark" />
    </ChartContent>
  </ChartCard>
</template>

<script setup>
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartContent from '@/components/insights/Chart/ChartContent.vue';
import BarChart from '@/components/insights/Chart/BarChart.vue';
import { computed } from 'vue';
import { useColors } from '@/composables/colors';
import { customTooltip } from '@/components/insights/Chart/oldChartTooltip';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { CanvasDrawBenchmarkPlugin } from '@/api/helpers/insights/chartPlugins';
import { benchmarkPluginNumericOffset, hiddenYScaleConfig } from '@/api/helpers/insights/commonChartConfigs';

const TIME_TO_HIRE_MAX = 50;
const TIME_TO_HIRE_MIN = 30;

const props = defineProps({
  timeToHire: {
    type: Number,
    required: true,
  },
  emptyData: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const widgetLabel = 'Time to Hire vs Benchmark';

const widgetId = 'time-to-hire-benchmark';

const {
  brandPrimary,
  yellow500,
  grey400,
  textDarkest,
  white,
} = useColors();

const timeToHireMax = computed(() => {
  const { timeToHire } = props || {};
  const maxValueToNextFifty = Math.ceil(timeToHire / 50) * 50;

  return maxValueToNextFifty > TIME_TO_HIRE_MAX
    ? maxValueToNextFifty
    : TIME_TO_HIRE_MAX;
});

const noWidgetData = computed(() => !props.timeToHire);

const chartData = computed(() => {
  const { timeToHire } = props || {};

  return {
    labels: ['Time to Hire'],
    datasets: [
      {
        label: '',
        backgroundColor: [
          brandPrimary.value,
          yellow500.value,
        ],
        borderColor: [
          brandPrimary.value,
          yellow500.value,
        ],
        fill: false,
        maxBarThickness: 24,
        barThickness: 'flex',
        data: [
          timeToHire,
        ],
      },
    ],
  };
});

const plugins = computed(() => [
  ChartDataLabels,
  CanvasDrawBenchmarkPlugin({
    benchmarkOptions: {
      min: TIME_TO_HIRE_MIN,
      max: TIME_TO_HIRE_MAX,
      ...benchmarkPluginNumericOffset,
    },
    scaleMax: timeToHireMax.value,
    chartId: `${widgetId}-chart`,
  }),
]);

const chartOptions = computed(() => ({
  maintainAspectRatio: false,
  responsive: true,
  indexAxis: 'y',
  plugins: {
    legend: { display: false },
    tooltip: {
      enabled: false,
      external: customTooltip(
        'basicChart',
        {
          chartData: {
            originalLabels: chartData.value.labels,
            datasets: chartData.value.datasets,
          },
          isFlippedAxis: true,
          labelFormat: 'count',
        },
        widgetId,
      ),
    },
    datalabels: {
      color: function colorCallback(context) {
        const max = timeToHireMax.value;
        const value = context.dataset.data[context.dataIndex];
        const percentOfSpace = (value / max) * 100;

        return percentOfSpace < 20 ? textDarkest.value : white.value;
      },
      anchor: 'end',
      align: function alignCallback(context) {
        const max = timeToHireMax.value;
        const value = context.dataset.data[context.dataIndex];
        const percentOfSpace = (value / max) * 100;

        if (Number.isNaN(percentOfSpace)) return 'end';

        return percentOfSpace < 20 ? 'end' : 'start';
      },
      formatter: function formatterCallback(value) {
        if (Number.isNaN(value)) return '';
        return `${value} days`;
      },
    },
  },
  scales: {
    x: {
      max: timeToHireMax.value,
      title: {
        display: false,
      },
      beginAtZero: true,
      border: {
        display: false,
        dash: [2, 3],
      },
      grid: {
        display: true,
        color: grey400.value,
        tickLength: 0,
      },
      ticks: {
        padding: 24,
        maxRotation: 0,
      },
    },
    y: hiddenYScaleConfig,
  },
}));
</script>

<style lang="less" scoped>
:deep(.chart-error-image) {
  height: 131px !important;
  max-height: 131px !important;
  padding: 16px 0px !important;
  margin: 0 !important;
}
</style>
